import { useCallback } from 'react';
import { Editor, Range as SlateRange } from 'slate';
import type { ReactEditor } from 'slate-react';

import type {
  SavedDocumentAnchor,
  SavedDocumentComment,
  SavedDocumentRating,
  SavedDocumentTextRating,
  SavedDocumentThread,
} from 'src/data/savedDocument';
import type { User } from 'src/data/user';
import type { VoidFunction } from 'src/lib/types';
// @ts-expect-error - no TypeScript definitions yet
import { useErrorHandler } from 'src/services/useErrorHandler';
// @ts-expect-error - no TypeScript definitions yet
import { useCurrentUser } from 'src/store/user';

import { type DocumentText, getAnchorFromRange, resolveAnchor } from './lib/anchors';
import { getRangeKey, type RatingGroup } from './lib/review';
import type { TextSelection } from './services/useTextSelection';
import { useDeleteCommentPopup } from './views/DeleteCommentPopup';
import { SAVED_DOCUMENT_PANEL_TYPE, useSavedDocumentViewActions } from './store';

interface Params {
  editor: ReactEditor;
  documentText: DocumentText;
  ratingGroups: RatingGroup[];
  selection: TextSelection | null;
  selectionAnchor: SavedDocumentAnchor | null;
  clearSelection: VoidFunction;
}

export const useHandlers = ({
  editor,
  documentText,
  ratingGroups,
  selection,
  selectionAnchor,
  clearSelection,
}: Params) => {
  const savedDocumentViewActions = useSavedDocumentViewActions();
  const handleError = useErrorHandler();
  const openDeleteCommentPopup = useDeleteCommentPopup();
  const currentUser: User | null = useCurrentUser();
  const currentUserId = currentUser ? String(currentUser.id) : undefined;

  // Each failure is shown to the user, except a lost access when the host handles it.
  const run = useCallback(
    async (action: () => Promise<unknown>): Promise<boolean> => {
      try {
        await action();
        return true;
      } catch (error) {
        handleError(error);
        return false;
      }
    },
    [handleError]
  );

  const handleStartComment = () => {
    if (!selection || !selectionAnchor) {
      return;
    }
    savedDocumentViewActions.setDraft({
      anchor: selectionAnchor,
      rangeRef: Editor.rangeRef(editor, selection.range, { affinity: 'inward' }),
      position: selection.position,
    });
    clearSelection();
  };

  // One rating per user on the same text: the same value again removes it, the other value
  // replaces it.
  const handleRateSelection = (value: SavedDocumentRating) => {
    if (!selection || !selectionAnchor) {
      return;
    }
    const resolved = resolveAnchor(editor, documentText, selectionAnchor);
    const key = resolved ? getRangeKey(resolved) : null;
    const mine = ratingGroups
      .find((group) => group.key === key)
      ?.ratings.find((rating) => rating.author.id === currentUserId);

    if (!mine) {
      run(() => savedDocumentViewActions.createTextRating(selectionAnchor, value));
    } else if (mine.value === value) {
      run(() => savedDocumentViewActions.deleteTextRating(mine.id));
    } else {
      run(() => savedDocumentViewActions.updateTextRating(mine.id, value));
    }
    if (key) {
      savedDocumentViewActions.setOpenPanel({
        type: SAVED_DOCUMENT_PANEL_TYPE.ratings,
        key,
        anchor: { top: selection.position.top, bottom: selection.position.bottom },
      });
    }
    clearSelection();
  };

  const handleSubmitComment = async (text: string) => {
    const draft = savedDocumentViewActions.getDraft();
    if (!draft) {
      return;
    }
    const range = draft.rangeRef.current;
    const anchor =
      (range &&
        !SlateRange.isCollapsed(range) &&
        getAnchorFromRange(editor, documentText, range)) ||
      draft.anchor;
    let threadId: string | undefined;
    const created = await run(async () => {
      threadId = await savedDocumentViewActions.createThread(anchor, text);
    });
    if (created && threadId) {
      savedDocumentViewActions.setOpenPanel({
        type: SAVED_DOCUMENT_PANEL_TYPE.thread,
        key: threadId,
        anchor: { top: draft.position.top, bottom: draft.position.bottom },
      });
    }
  };

  const handleReply = (threadId: string, text: string) =>
    run(() => savedDocumentViewActions.replyToThread(threadId, text));

  const handleResolveThread = (threadId: string) => {
    run(() => savedDocumentViewActions.resolveThread(threadId));
  };

  const handleEditComment = (commentId: string, text: string) => {
    run(() => savedDocumentViewActions.updateComment(commentId, text));
  };

  // Deleting the first comment deletes the thread with its replies.
  const handleDeleteComment = async (
    thread: SavedDocumentThread,
    comment: SavedDocumentComment
  ) => {
    const replyCount = thread.comments[0]?.id === comment.id ? thread.comments.length - 1 : 0;
    if (await openDeleteCommentPopup(replyCount)) {
      run(() => savedDocumentViewActions.deleteComment(comment.id));
    }
  };

  const handleRemoveTextRating = (rating: SavedDocumentTextRating) => {
    run(() => savedDocumentViewActions.deleteTextRating(rating.id));
  };

  return {
    currentUser,
    currentUserId,
    handleStartComment,
    handleRateSelection,
    handleSubmitComment,
    handleReply,
    handleResolveThread,
    handleEditComment,
    handleDeleteComment,
    handleRemoveTextRating,
  };
};
